import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { data_download_api_resource } from "../../utils/http_functions";

import 'dayjs/locale/ca';
import 'dayjs/locale/es';
import 'dayjs/locale/en-gb';

import {
  Button, CircularProgress, MenuItem,
  Select, FormControl, InputLabel, Grid
} from "@mui/material";
import { GetApp, Search } from "@mui/icons-material"

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { dayjs } from "../../config";

import saveAs from 'file-saver';

const DateSelector = ({ onDatesChange, contractId, defaults, loading, endpointVersion }) => {
  const { i18n, t } = useTranslation();
  const token = useSelector((state) => state.auth.token);

  const [type, setType] = useState(defaults.type);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [downloading, setDownloading] = useState(false);
  const [downloadAvailable, setDownloadAvailable] = useState(true);

  useEffect(() => {
    if (defaults.startDate) {
      setStartDate(defaults.startDate);
    }
    if (defaults.endDate) {
      setEndDate(defaults.endDate);
    }
    if (defaults.type) {
      setType(defaults.type);
    }
  }, [defaults]);

  const langDateFormat = { 'es-ES': "es", 'ca-ES': "ca", 'en-EN': "en-gb" }

  const handleChangeType = (event) => {
    setType(event.target.value);

    let newStartDate = dayjs(startDate);
    let newEndDate = dayjs(endDate);

    switch (event.target.value) {
      case "monthly":
        newStartDate = dayjs().month(dayjs().month() - 12).startOf("month");
        newEndDate = dayjs().endOf("month");
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        onDatesChange({
          type: 'monthly',
          startDate: newStartDate,
          endDate: newEndDate
        });
        break;

      case "hourly":
      case "quarter-hourly":
        newStartDate = dayjs().subtract(7, "day").startOf("day").startOf("hour");
        newEndDate = dayjs().endOf("day").minute(45);
        break;

      case "daily":
        newStartDate = dayjs().startOf("month");
        newEndDate = dayjs().endOf("month");
        break;
    }
    setStartDate(newStartDate);
    setEndDate(newEndDate)
  };

  const handleStartChange = (date) => {
    switch (type) {
      case "monthly":
        setStartDate(date.startOf('month').startOf('day'));
        break;
      case "hourly":
        setStartDate(date.startOf('hour'));
        break;
      case "quarter-hourly":
        setStartDate(date.startOf('second'));
        break;
      default:
        setStartDate(date);
    }
  };

  const handleEndChange = (date) => {
    switch (type) {
      case "monthly":
        setEndDate(date.endOf('month').endOf('day'));
        break;
      case "hourly":
        setEndDate(date.startOf('hour'));
        break;
      case "quarter-hourly":
        setEndDate(date.startOf('second'));
        break;
      default:
        setEndDate(date);
    }
  };

  const handleFetchDates = (
    newType = type
  ) => {
    onDatesChange({
      type: newType,
      startDate,
      endDate
    });

    setDownloadAvailable(true);
  }

  const downloadCCH = () => {
    setDownloading(true);

    const url = `contracts/${contractId}/cch/download/${type}/` +
      `?start=${dayjs(startDate).utc(true).toISOString()}` +
      `&end=${dayjs(endDate).utc(true).toISOString()}`;

    data_download_api_resource(token, url, endpointVersion)
      .then(response => {
        const filename = response.headers["content-disposition"].split("=");
        saveAs(response.data, filename[1]);
      })
      .finally(() => {
        setDownloading(false);
      })
  };

  return (
    <FormControl sx={{ width: "100%" }}>
      <Grid container alignItems="center" spacing={2}>

        <Grid item xs={12} md="auto">
          <InputLabel>{t('common:text.cch_visualization')}</InputLabel>
          <Select onChange={handleChangeType} value={type}>

            <MenuItem key="monthly" value="monthly">
              {t('common:text.cch_monthly')}
            </MenuItem>
            <MenuItem key="daily" value="daily">
              {t('common:text.cch_daily')}
            </MenuItem>
            <MenuItem key="hourly" value="hourly">
              {t('common:text.cch_hourly')}
            </MenuItem>
            <MenuItem key="quarter-hourly" value="quarter-hourly">
              {t('common:text.cch_quarter_hourly')}
            </MenuItem>

          </Select>
        </Grid>

        <Grid container item spacing={2} xs={12} md="auto">
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={langDateFormat[i18n.language]}
          >

            {['monthly'].includes(type) &&
              <>
                <Grid item>
                  <DatePicker
                    disableToolbar
                    error={startDate == null || startDate == "Invalid Date"}
                    helperText={
                      startDate == null || startDate == "Invalid Date" ? 
                      t('common:text.cch_invalid_date') : ""
                    }
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    label={t("common:text.cch_start")}
                    maxDate={endDate}
                    onChange={handleStartChange}
                    value={startDate}
                    disabled={loading}
                    variant="inline"
                    views={['month', 'year']}
                  />
                </Grid>

                <Grid item>
                  <DatePicker
                    disableToolbar
                    error={endDate == null || endDate == "Invalid Date"}
                    helperText={
                      endDate == null || endDate == "Invalid Date" ? 
                      t('common:text.cch_invalid_date') : ""
                    }
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    label={t("common:text.cch_end")}
                    minDate={startDate}
                    onChange={handleEndChange}
                    value={endDate}
                    disabled={loading}
                    variant="inline"
                    views={['month', 'year']}
                  />
                </Grid>
              </>
            }

            {['daily'].includes(type) &&
              <>
                <Grid item>
                  <DatePicker
                    disableToolbar
                    error={startDate == null || startDate == "Invalid Date"}
                    helperText={
                      startDate == null || startDate == "Invalid Date" ?
                        t('common:text.cch_invalid_date') : ""
                    }
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    label={t("common:text.cch_start")}
                    maxDate={endDate}
                    onChange={handleStartChange}
                    value={startDate}
                    disabled={loading}
                    variant="inline"
                  />
                </Grid>

                <Grid item>
                  <DatePicker
                    disableToolbar
                    error={endDate == null || endDate == "Invalid Date"}
                    helperText={
                      endDate == null || endDate == "Invalid Date" ?
                        t('common:text.cch_invalid_date') : ""
                    }
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    label={t("common:text.cch_end")}
                    minDate={startDate}
                    onChange={handleEndChange}
                    value={endDate}
                    disabled={loading}
                    variant="inline"
                  />
                </Grid>
              </>
            }

            {['hourly', 'quarter-hourly'].includes(type) &&
              <>
                <Grid item>
                  <DateTimePicker
                    disableToolbar
                    error={startDate == null || startDate == "Invalid Date"}
                    helperText={
                      startDate == null || startDate == "Invalid Date" ?
                        t('common:text.cch_invalid_date') : ""
                    }
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    label={t("common:text.cch_start")}
                    maxDate={endDate}
                    onChange={handleStartChange}
                    value={startDate}
                    disabled={loading}
                    variant="inline"
                    minutesStep={type === 'quarter-hourly' ? 15 : 1}
                    views={
                      ['year', 'month', 'day'].concat(
                        (type === 'hourly' ? ['hours'] :
                          (type === 'quarter-hourly' ? ['hours', 'minutes'] : [])
                        )
                      )
                    }
                  />
                </Grid>

                <Grid item>
                  <DateTimePicker
                    disableToolbar
                    error={endDate == null || endDate == "Invalid Date"}
                    helperText={
                      endDate == null || endDate == "Invalid Date" ?
                        t('common:text.cch_invalid_date') : ""
                    }
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    label={t("common:text.cch_end")}
                    minDate={startDate}
                    onChange={handleEndChange}
                    value={endDate}
                    disabled={loading}
                    variant="inline"
                    minutesStep={type === 'quarter-hourly' ? 15 : 1}
                    views={
                      ['year', 'month', 'day'].concat(
                        (type === 'hourly' ? ['hours'] :
                          (type === 'quarter-hourly' ? ['hours', 'minutes'] : [])
                        )
                      )
                    }
                  />
                </Grid>
              </>
            }

          </LocalizationProvider>
        </Grid>

        <Grid container item spacing={2} xs={12} md="auto">

          {['monthly', 'daily', 'hourly', 'quarter-hourly'].includes(type) &&
            <Grid item>
              <Button
                variant={'contained'}
                color={'primary'}
                onClick={() => handleFetchDates()}
                disabled={loading}
                startIcon={<Search />}
              >
                {t('common:text.contract_cch_search')}
              </Button>
            </Grid>
          }

          <Grid item>
            <Button
              variant={'contained'}
              onClick={downloadCCH}
              disabled={!downloadAvailable || downloading || loading}
              startIcon={downloading ?
                <CircularProgress size={20} thickness={3} /> : <GetApp />
              }
            >
              {t('common:text.contract_cch_download')}
            </Button>
          </Grid>

        </Grid>

      </Grid>
    </FormControl>
  )
}

export default DateSelector;